/* eslint-disable react/prop-types */
/* eslint-disable react/function-component-definition */
/**
=========================================================
* Material Dashboard 2 React - Territory Management
=========================================================
*/

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import useTerritory from "hooks/useTerritory";
import { useEffect } from "react";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import useSales from "hooks/useSales";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import {
  Autocomplete,
  Avatar,
  AvatarGroup,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Tooltip,
  useMediaQuery,
  useTheme,
} from "@mui/material";

export default function data() {
  const navigate = useNavigate();
  const { getTerritoryList, deleteTerritory, assignSalesRep, territoryList } = useTerritory();
  const { getSalesOptionList } = useSales();
  const [data, setData] = useState([]);
  const [salesList, setSalesList] = useState([]);
  const [open, setOpen] = useState(false);
  const [selectedTerritory, setSelectedTerritory] = useState(null);

  const handleClickOpen = (territory) => {
    setSelectedTerritory(territory);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setSelectedTerritory(null);
  };

  const TerritoryActions = ({ onEdit, onDelete, onAssign }) => {
    return (
      <MDBox display="flex" justifyContent="center">
        <MDButton variant="contained" color="dark" onClick={onAssign} sx={{ mr: 1 }}>
          Assign Sales Team
        </MDButton>
        <MDButton variant="contained" color="info" onClick={onEdit} sx={{ mr: 1 }}>
          <EditIcon />
        </MDButton>
        <MDButton variant="contained" color="error" onClick={onDelete}>
          <DeleteIcon />
        </MDButton>
        <ResponsiveDialog
          open={open}
          onClose={handleClose}
          salesList={salesList}
          selectedTerritory={selectedTerritory}
          assignSalesRep={assignSalesRep}
          fetchList={fetchList}
        />
      </MDBox>
    );
  };

  const TerritoryInfo = ({ name }) => (
    <MDBox display="flex" alignItems="center" lineHeight={1}>
      <MDBox ml={2} lineHeight={1}>
        <MDTypography display="block" variant="button" fontWeight="medium">
          {name}
        </MDTypography>
      </MDBox>
    </MDBox>
  );

  const getInitials = (name) => name.charAt(0).toUpperCase();

  const classicColors = [
    "#3B82F6", // Standard Blue
    "#475569", // Soft Dark Gray
    "#2563EB", // Bright Blue
    "#1E293B", // Almost Black-Gray
    "#1E3A8A", // Navy Blue
    "#4F46E5", // Indigo
    "#0369A1", // Deep Blue
    "#334155", // Dark Gray
    "#1E40AF", // Royal Blue
    "#64748B", // Muted Gray-Blue
  ];

  // Function to get color based on index
  const getColor = (index) => classicColors[index % classicColors.length];

  const SalesTeam = ({ sales = [] }) => {
    return (
      <MDBox display="flex" alignItems="center">
        <AvatarGroup max={4} sx={{ flexDirection: "row-reverse" }}>
          {sales.map((item, index) => (
            <Tooltip key={item?.userId?.name} title={item?.userId?.name} arrow>
              <Avatar
                sx={{
                  bgcolor: getColor(index),
                  color: "white",
                  fontWeight: "bold",
                  cursor: "pointer",
                  transition: "transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out",
                  "&:hover": {
                    transform: "scale(1.2)",
                    boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.2)",
                  },
                }}
              >
                {item?.userId?.img ? (
                  <img src={item?.userId?.img} alt={item?.userId?.name} />
                ) : (
                  getInitials(item?.userId?.name)
                )}
              </Avatar>
            </Tooltip>
          ))}
        </AvatarGroup>
      </MDBox>
    );
  };

  const rows = data?.map((territory) => ({
    territory: <TerritoryInfo name={territory.name} />,
    slug: territory.slug,
    town: territory.town,
    sales: <SalesTeam sales={territory.sales} />,
    actions: (
      <TerritoryActions
        onEdit={() => navigate(`/territory-management/edit?id=${territory._id}`)}
        onDelete={() => {
          if (window.confirm("Are you sure you want to delete this territory?")) {
            if (territory.sales.length > 0) {
              alert("You need to reassign the sales team before deleting this territory.");
              return;
            }
            deleteTerritory(territory._id).then((res) => {
              if (res.success) {
                fetchList();
              }
            });
          }
        }}
        onAssign={() => handleClickOpen(territory)}
      />
    ),
  }));

  const fetchList = async () => {
    await getTerritoryList();
  };

  const fetchSalesList = async () => {
    const response = await getSalesOptionList();
    if (response.success) {
      setSalesList(response.data);
    }
  };

  useEffect(() => {
    // fetchList();
    fetchSalesList();
  }, []);

  useEffect(() => {
    if (territoryList.territories) {
      setData(territoryList.territories);
    }
  }, [territoryList]);
  return {
    columns: [
      { Header: "Territory", accessor: "territory", width: "30%", align: "left" },
      { Header: "Slug", accessor: "slug", align: "left" },
      { Header: "Town", accessor: "town", align: "left" },
      { Header: "Sales Team", accessor: "sales", align: "left" },
      { Header: "Actions", accessor: "actions", align: "center" },
    ],
    rows,
  };
}

function ResponsiveDialog({
  open,
  onClose,
  salesList,
  selectedTerritory,
  assignSalesRep,
  fetchList,
}) {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("md"));
  const [selectedSalesIds, setSelectedDealerIds] = useState([]);

  useEffect(() => {
    if (selectedTerritory?.sales) {
      const preSelectedIds = selectedTerritory.sales.map((sales) => sales._id);
      setSelectedDealerIds(preSelectedIds);
    }
  }, [selectedTerritory, salesList, open]);

  const handleSalesChange = (event, selectedDealers) => {
    const selectedIds = selectedDealers.map((sales) => sales._id);
    setSelectedDealerIds(selectedIds);
  };

  const handleAssignDealer = async () => {
    try {
      const response = await assignSalesRep(selectedTerritory._id, {
        salesRepIds: selectedSalesIds,
      });
      if (response.success) {
        fetchList(); // Refresh data after assigning
        onClose();
      } else {
        alert(response.message || "Error assigning dealers");
      }
    } catch (error) {
      console.error("Error assigning dealers:", error);
      alert("An unexpected error occurred.");
    }
  };
  return (
    <Dialog
      fullScreen={fullScreen}
      open={open}
      // onClose={onClose}
      BackdropProps={{
        style: { backgroundColor: "hsla(0, 0.00%, 0.00%, 0.12)" }, // Light transparent background
      }}
    >
      <DialogTitle id="responsive-dialog-title">Assign Sales</DialogTitle>
      <DialogContent>
        <Autocomplete
          multiple
          id="checkboxes-tags-demo"
          options={salesList}
          disableCloseOnSelect
          getOptionLabel={(option) => option.userId.name}
          value={salesList.filter((sales) => selectedSalesIds.includes(sales._id))}
          onChange={handleSalesChange}
          renderOption={(props, option, { selected }) => {
            const { key, ...optionProps } = props;
            return (
              <li key={key} {...optionProps}>
                <Checkbox
                  icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
                  checkedIcon={<CheckBoxIcon fontSize="small" />}
                  style={{ marginRight: 8 }}
                  checked={selected}
                />
                {option.userId.name}
              </li>
            );
          }}
          style={{ width: 500 }}
          renderInput={(params) => <TextField {...params} placeholder="Select Sales Rep" />}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleAssignDealer} autoFocus>
          Assign
        </Button>
      </DialogActions>
    </Dialog>
  );
}
